import { Anchor } from '@components/atoms/Anchor';
import Hr from '@components/atoms/Hr';
import Section from '@components/atoms/Section';
import { Heading2, Paragraph } from '@components/atoms/Typography';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { TableOfContents } from '@components/molecules/slices/TableOfContents';
import {
  ButtonOnRichText,
  SliceButtonLink,
  SliceVehicleModels,
} from '@components/molecules/SliceZone';
import DefaultTable from '@components/molecules/table/DefaultTable';
import FixedGetQuoteButton from '@components/organisms/buttons/FixedGetQuoteButton';
import CarriersLogoList from '@components/organisms/CarriersLogoList';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import JoinSection from '@components/organisms/sections/JoinSection';
import FAQBlock from '@components/organisms/seo/FAQBlock';
import { CoverageType } from '@graphql/generated';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import LINKS from '@lib/constants/links';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import carriersContent from '@lib/data/contents/carriersContent';
import pricesPathMigration from '@lib/data/prices/pricesPathMigration';
import { integerToCurrency } from '@lib/helpers/misc';
import {
  getCoverageInfo,
  getInsurancePageInfo,
  InsurancePageId,
} from '@lib/helpers/texts';
import prisma from '@lib/services/prisma';
import { FipeModel, InsurancePrice } from '@prisma/client';
import { joinWords } from 'helpers';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd, ProductJsonLd } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <DefaultHeader
    title={title}
    subtitle={subtitle}
    pastel
    buttonText="Simular Seguro Online"
  />
);

interface Props {
  vehicles: (FipeModel & {
    insurancePrices: InsurancePrice[];
  })[];
  id: InsurancePageId;
  models: {
    modelSlug: string;
    model: string;
  }[];
}

const CarModel = ({ vehicles, id, models }: Props) => {
  const router = useRouter();

  const pageInfo = getInsurancePageInfo(id);

  if (router.isFallback || !vehicles.length) return <PageContainer loading />;

  const vehicle = vehicles[0];

  const variations = vehicle.model.split('/');

  const modelName = variations[0].trim();

  const brandModel = `${vehicle.brand} ${modelName}`;

  // const groupedByYear = groupBy(priceInfo, (p) => p.quoteInput.year);

  const insurancePrices = vehicles.map((v) => v.insurancePrices).flat();

  const avgPrice =
    insurancePrices.reduce(
      (prev, curr) => prev + curr.theft + curr.totalLoss,
      0,
    ) /
    (insurancePrices.length * 2);

  const carriers = Object.values(carriersContent).filter((c) =>
    c.products.some((p) => p.pageId === id),
  );

  const heads = [
    { id: 'h-1', text: `Quanto custa o seguro da ${brandModel}?` },
    { id: 'h-2', text: `Como contratar seguro para ${brandModel}?` },
    { id: 'h-3', text: `Como renovar o seguro para ${brandModel}?` },
    { id: 'h-4', text: `Qual a melhor seguradora para ${brandModel}?` },
    { id: 'h-5', text: `O que cobre o seguro da ${brandModel}?` },
  ];

  return (
    <PageContainer
      customAmpPage
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      title={`Quanto custa o seguro ${brandModel}`}
      metaDescription={`Descubra quanto custa o seguro ${brandModel}, preço médio, renovação, coberturas e muito mais no ${pageInfo?.productName} para ${brandModel}`}
    >
      <ArticleJsonLd
        url={`${env.baseUrl}/${ROUTES.insurancePage.model(
          id,
          vehicle.brandSlug,
          vehicle.modelSlug,
        )}`}
        title={`Quanto custa o seguro ${brandModel}`}
        description={`Descubra quanto custa o seguro da ${brandModel} na principais cidades do país, e veja quanto custa o seguro do seu ${pageInfo?.name}`}
        datePublished="2021-10-10T03:00:00.000Z"
        dateModified="2023-08-15T03:00:00.000Z"
        authorName={['Luna Seguros']}
        publisherName="Luna Seguros"
        publisherLogo={LINKS.logo}
        images={[LINKS.ogImage]}
      />
      <ProductJsonLd
        productName={`Seguro ${brandModel}`}
        description={`Seguro ${brandModel} online e barato. Compare os preços das melhores seguradoras de ${pageInfo?.name}`}
        aggregateRating={aggregateRating}
      />
      <Header
        title={`Preço do seguro para ${brandModel}`}
        subtitle={`Cote o seguro para ${brandModel} online e compare o preço de várias seguradoras com a Luna`}
      />
      <CarriersLogoList altPrefix={`Seguro ${brandModel}`} />
      <RichTextSection>
        <Paragraph>
          Você que já tem um {brandModel} ou está pensando em comprar, ou até
          mesmo{' '}
          <Link
            href={ROUTES.financiamento.model({
              type: vehicle.vehicleType,
              brand: vehicle.brandSlug,
              model: vehicle.modelSlug,
            })}
            passHref
          >
            <Anchor>financiar um {brandModel}</Anchor>
          </Link>
          , esse conteúdo vai te mostrar tudo que precisa saber sobre o{' '}
          {pageInfo?.productName}.
        </Paragraph>
        <p>
          Saiba quanto custa o seguro <strong>{brandModel}</strong>, como
          contratar, renovar e muito mais.
        </p>
        <TableOfContents
          slice={{ primary: { title1: 'Resumo' } }}
          heads={heads}
        />
        {variations.length > 1 && (
          <>
            <p>
              A {brandModel} tem {variations.length} variações{' '}
              {joinWords(variations)}. O Valor do seguro para as{' '}
              {variations.length} variações é o mesmo, pois elas tem o mesmo
              valor de mercado.
            </p>
          </>
        )}
        {insurancePrices.length ? (
          <>
            <p>
              Sendo assim, para não ficar na dúvida, separamos o preço médio do
              seguro de {pageInfo?.name.toLowerCase()} aqui na Luna - baseado em
              um homem casado de 35 anos.
            </p>
            <h2 id={heads[0].id}>{heads[0].text}</h2>
            <p>
              Considerando o perfil acima,{' '}
              <strong>
                o valor médio do seguro para {brandModel} é de{' '}
                {integerToCurrency(avgPrice)}.
              </strong>
            </p>
            <p>
              Agora pra saber o valor exato do seguro {brandModel}, faça uma
              cotacão do seguro online com a Luna, em menos de 3 minutos você
              vai conseguir ver o preço das melhores seguradoras.
            </p>
            <ButtonOnRichText text={`Simular Seguro ${vehicle.brand}`} />
          </>
        ) : (
          <>
            <h2 id="h-1">Quanto custa o seguro da {brandModel}?</h2>
            <p>
              Faça uma cotacão do seguro online com a Luna, em menos de 3
              minutos você vai conseguir ver o preço das melhores seguradoras.
            </p>
            <ButtonOnRichText text={`Simular Seguro ${vehicle.brand}`} />
          </>
        )}
        <h2 id={heads[1].id}>{heads[1].text}</h2>
        <p>
          Todas as pessoas donas de {brandModel}, podem contratar um seguro
          facilmente. Basta seguir os passos abaixos para contratar o{' '}
          {pageInfo?.productName}.
        </p>
        <ul>
          <li>
            <Link href={ROUTES.startForm()}>
              <a>Inicie a contratação do seguro para {brandModel} online</a>
            </Link>
          </li>
          <li>Informe os detalhes de como você usa a {pageInfo?.name}</li>
          <li>Responda sobre quem é o condutor</li>
          <li>Informe os dados de contato</li>
          <li>
            Vários ofertas de seguro de várias seguradoras serão mostradas
          </li>
          <li>
            Escolha o melhor seguro para {brandModel} conforme a sua necessidade
          </li>
          <li>
            Realize o pagamento do seguro para {brandModel} via boleto, débito
            em conta ou cartão de crédito
          </li>
          <li>
            A sua seguradora para {brandModel} vai emitir a sua apólice online
            🎉
          </li>
        </ul>
        <h2 id={heads[2].id}>{heads[2].text}</h2>
        <p>
          Todo seguro de {brandModel} tem uma de expiração. Os dono de{' '}
          {pageInfo?.name} devem se lembrar de renovar o seguro {brandModel}{' '}
          antes do fim da vigência para continuar com sua proteção
        </p>
        <ul>
          <li>
            <Link href={ROUTES.startForm()}>
              <a>Inicie a renovação do seguro para {brandModel} online</a>
            </Link>
          </li>
          <li>Informe qual sua seguradora atual de {pageInfo?.name}</li>
          <li>Informe os dados da sua apólice atual</li>
          <li>
            Vamos buscar as melhores ofertas de renovação de seguro para{' '}
            {brandModel}
          </li>
          <li>Escolha a melhor renovação de seguro para {brandModel}.</li>
          <li>
            A sua seguradora para {brandModel} vai emitir a sua renovação de
            apólice online 🎉
          </li>
        </ul>
        <h3>Quanto custa para renovar o seguro {brandModel}?</h3>
        <p>
          O valor de renovação do seguro {pageInfo?.name} depende da sua classe
          bônus e quantos sinistros você teve no seu último seguro, para saber o
          valor exato a melhor coisa é cotar a renovação do seguro {brandModel}{' '}
          online
        </p>
        <ButtonOnRichText text={`Renovar Seguro ${vehicle.brand}`} />
        <h2 id={heads[3].id}>{heads[3].text}</h2>
        <p>
          As melhores{' '}
          <Link href={ROUTES.insurancePage.carriers(id)}>
            <a>seguradoras para {brandModel}</a>
          </Link>{' '}
          dependem de vários fatores, principalmente que tipo de cobertura você
          está buscando e como você usa o veículo.
        </p>
        {id === 'seguro-moto' && (
          <>
            <Link href="/blog/melhores-seguradoras-para-motos">
              <a>Confira o ranking do melhor seguro de moto em 2022</a>
            </Link>
          </>
        )}
        <p>As seguradoras que fazem seguro para {brandModel} são: </p>
        <ul>
          {carriers.map((c) => (
            <li key={c.id}>
              <Link href={ROUTES.insurancePage.carrier(id, c.id)}>
                <a>
                  Seguro {brandModel} na {c.name}
                </a>
              </Link>
            </li>
          ))}
        </ul>
        <h2 id={heads[4].id}>{heads[4].text}</h2>{' '}
        <p>
          O seguro de {brandModel} é igual ao seguro de qualquer{' '}
          {pageInfo?.name}, no {pageInfo?.productName} as coberturas oferecidas
          pelas seguradoras são:
        </p>
        <ul>
          {[
            CoverageType.Colision,
            CoverageType.Theft,
            CoverageType.Fire,
            CoverageType.Nature,
            CoverageType.TotalLoss,

            CoverageType.ThirdPartyMaterial,
            CoverageType.ThirdPartyBody,
            CoverageType.MoralDamages,
            CoverageType.AppDeath,
            CoverageType.AppInvalid,
          ].map((c) => (
            <li key={c}>{getCoverageInfo({ type: c, value: 1 }).name}</li>
          ))}
        </ul>
        <p>
          As coberturas do seguro {brandModel} podem ser personalizadas conforme
          a sua seguradora de {pageInfo?.name}
        </p>
        {insurancePrices.length > 0 && (
          <>
            <Heading2>Preço médio do seguro {brandModel}</Heading2>
            <Paragraph>
              Confira o valor do seguro para cada ano da {brandModel}, separamos
              os valores do seguro completo e do seguro mais básico nas
              principais cidades do Brasil.
            </Paragraph>

            {vehicles
              .filter((v) => !!v.insurancePrices.length)
              .sort((a, b) => b.modelYear - a.modelYear)
              .map((v) => {
                const sorted = v.insurancePrices.sort(
                  (a, b) => a.theft + a.totalLoss - (b.theft + b.totalLoss),
                );

                const cheaper = sorted[0];
                const expensive = sorted[sorted.length - 1];

                return (
                  <div id={`${v.modelYear}`} key={v.modelYear}>
                    <h3 style={{ marginBottom: 16 }}>
                      {`Preço seguro ${brandModel} ${v.modelYear}`}
                    </h3>
                    <Paragraph>
                      O custo médio do seguro para {brandModel} {v.modelYear} é
                      de{' '}
                      <strong>
                        {integerToCurrency(
                          Math.round(
                            v.insurancePrices.reduce(
                              (acc, curr) => acc + curr.theft + curr.totalLoss,
                              0,
                            ) /
                              (v.insurancePrices.length * 2),
                          ),
                        )}
                      </strong>
                      . Sendo que a cidade onde o{' '}
                      <strong>seguro é mais barato</strong> é{' '}
                      {`${cheaper.city}-${cheaper.uf} `}
                      com um{' '}
                      <strong>
                        valor médio de{' '}
                        {integerToCurrency(
                          (cheaper.theft + cheaper.totalLoss) / 2,
                        )}{' '}
                      </strong>
                      reais e a cidade com o seguro mais caro é{' '}
                      {`${expensive.city}-${expensive.uf} `} com um valor de{' '}
                      {integerToCurrency(
                        (expensive.theft + expensive.totalLoss) / 2,
                      )}{' '}
                      reais.
                    </Paragraph>
                    <Paragraph>
                      Confira o preço médio anual da {brandModel} {v.modelYear}{' '}
                      em cada cidade:
                    </Paragraph>
                    <DefaultTable
                      heads={[
                        { text: 'Estado' },
                        { text: 'Cidade' },
                        { text: 'Roubo e furto' },
                        { text: 'Cobertura completa' },
                      ]}
                      rows={v.insurancePrices.map((info) => [
                        { text: info.uf },
                        { text: info.city },
                        { text: integerToCurrency(info.theft) },
                        { text: integerToCurrency(info.totalLoss) },
                      ])}
                    />
                    <Paragraph>
                      Ficou interessado no seguro para {brandModel}{' '}
                      {v.modelYear}?
                    </Paragraph>
                    <SliceButtonLink
                      slice={{
                        primary: {
                          href: ROUTES.startForm(),
                          text: 'Simule Agora',
                        },
                      }}
                    />
                    <Hr bgColor="#d2d3d4" />
                  </div>
                );
              })}
          </>
        )}
        <FAQBlock
          title={`Perguntas frequentes sobre o seguro da ${vehicle.model}`}
          items={[
            {
              question: `Como cotar seguro ${brandModel} online?`,
              answer: (
                <>
                  <p>Para cotar o seguro é bem simples</p>
                  <ol>
                    <li>
                      Use o{' '}
                      <Link href={ROUTES.startForm()}>
                        <a>cotador de {pageInfo?.productName} online</a>
                      </Link>{' '}
                      da Luna
                    </li>
                    <li>
                      Informe como você usa o <strong>{brandModel}</strong>
                    </li>
                    <li>Informe alguns dados sobre você</li>
                  </ol>
                  <p>
                    Depois disso você vai encontrar as{' '}
                    <strong>
                      melhores seguradoras para o seu {brandModel}
                    </strong>{' '}
                    de forma totalmente online{' '}
                  </p>
                </>
              ),
            },
            {
              question: `Quanto custa pra renovar o seguro ${vehicle.brand} ${vehicle.model}?`,
              answer: (
                <>
                  <Paragraph>
                    O custo de{' '}
                    <Link href={ROUTES.startForm()} passHref>
                      <Anchor>
                        renovação do seguro{' '}
                        <strong>
                          {vehicle.brand} {vehicle.model}
                        </strong>
                      </Anchor>
                    </Link>{' '}
                    depende da sua classe bônus atual, quanto mais alta a classe
                    bônus maior o desconto no seguro.
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Qual o melhor seguro para ${vehicle.brand} ${vehicle.model}?`,
              answer: (
                <Paragraph>
                  O melhor seguro para{' '}
                  <strong>
                    {vehicle.brand} {vehicle.model}
                  </strong>{' '}
                  varia conforme a sua necessidade, aqui na Luna você consegue{' '}
                  <Link href={ROUTES.startForm()} passHref>
                    <Anchor>cotar em várias seguradoras</Anchor>
                  </Link>{' '}
                  e comparar o melhor preço para você.
                </Paragraph>
              ),
            },
            {
              question: `Seguro para ${vehicle.brand} ${vehicle.model} barato?`,
              answer: (
                <>
                  <Paragraph>
                    Com a Luna você consegue cotar o seguro para{' '}
                    <strong>
                      {vehicle.brand} {vehicle.model}
                    </strong>{' '}
                    <Link href={ROUTES.carriers.home} passHref>
                      <Anchor>nas melhores seguradoras do mercado</Anchor>
                    </Link>{' '}
                    e ver o preço mais barato para você.
                  </Paragraph>
                </>
              ),
            },
            {
              question: `Como contratar seguro para ${vehicle.brand} ${vehicle.model} online?`,
              answer: (
                <>
                  <Paragraph>
                    Muito simples, basta realizar uma{' '}
                    <Link href={ROUTES.startForm()} passHref>
                      <Anchor>
                        cotação de seguro para{' '}
                        <strong>
                          {vehicle.brand} {vehicle.model}
                        </strong>
                      </Anchor>
                    </Link>{' '}
                    no site da Luna, escolher a seguradora e contratar!
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
        <Heading2>Confira outros modelos da {vehicle.brand}</Heading2>
        <SliceVehicleModels
          slice={{
            models,
            brand: vehicle.brand,
            pageId: id,
          }}
        />
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: '/',
            },
            {
              label: pageInfo?.productName || '',
              href: ROUTES.insurancePage.home(id),
            },
            {
              label: vehicle.brand,
              href: ROUTES.insurancePage.brand(id, vehicle.brandSlug),
            },
            {
              label: vehicle.model,
              href: ROUTES.insurancePage.model(
                id,
                vehicle.brand,
                vehicle.modelSlug,
              ),
            },
          ]}
        />
      </Section>
      <JoinSection
        buttonText="Ver Preços"
        title={`Seguro para ${vehicle.brand} ${vehicle.model}`}
        description={`Cotação de seguro para ${vehicle.brand} ${vehicle.model} de forma totalmente digital!`}
        href={ROUTES.startForm()}
      />
      <FixedGetQuoteButton />
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [
    '/seguro-carro/audi/a3-1-6-3p',
    '/seguro-moto/honda/adv-150',
    '/seguro-carro/volvo/s60-t-6-3-0-304cv-awd-4p',
  ],
  fallback: true,
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { insurancePage } = params as {
    insurancePage: InsurancePageId;
  };

  const model = params?.model?.toString().toLowerCase() as string;

  const brand = params?.brand?.toString().toLowerCase() as string;

  const pathChanged =
    pricesPathMigration[
      ROUTES.insurancePage.model(insurancePage, brand, model)
    ] || null;

  if (pathChanged) {
    return {
      redirect: {
        destination: pathChanged,
        permanent: true,
      },
    };
  }

  const info = getInsurancePageInfo(insurancePage);

  if (!model || !info) return { notFound: true };

  const [vehicles, brandModels] = await Promise.all([
    prisma.fipeModel.findMany({
      where: {
        brandSlug: brand,
        modelSlug: model,
        vehicleType: info.type,
      },
      include: {
        insurancePrices: true,
      },
    }),
    prisma.fipeModel.findMany({
      where: {
        brandSlug: brand,

        vehicleType: info.type,
      },
      distinct: ['fipeCode'],
      orderBy: { model: 'asc' },
      select: {
        model: true,
        modelSlug: true,
      },
    }),
  ]);

  if (!vehicles.length) return { notFound: true };

  return {
    props: {
      vehicles,
      id: insurancePage,
      models: brandModels,
    },
  };
};

export default CarModel;
